import { formatPrice, FormatPriceOptions } from '../../../../utils/format-price'
import { COLOR_PROPERTY } from './color'
import { AttributeSelectorMarker } from '../../common/config'
import { type Stocks, stockStatusMap, getStockLevelStatus } from './stock'
import { getMatchedVariant, type HistoryFilter } from './filter'
import {
  ProductDataVariant,
  VariantWithDynamicAttributes
} from '../../../../services/product/product-data'
import { StockLevelStatus } from '../../../../services/stock'
import { isMinQuantityExceedsStock } from '../../../../utils/quantity-selector'

type AttrDataAux =
  | {
      propertyName: string
      attrIndex: number
      values: {
        [x: string]: AttrDataAux
      }
    }
  | object

type VariantAttrValues = {
  [x: string]: string
}

export type AttributeSelector = {
  name: string
  label?: string
  showPrice?: boolean
}

type SelectorValues = {
  [x: string]: {
    attrIndex: number
    values: string[]
  }
}

type VariantValues = { [x: string]: string }

export const getVariantAttrData = (
  attrList: AttributeSelector[],
  variant: ProductDataVariant
): VariantValues =>
  attrList?.reduce((acc, curr) => {
    const { name } = curr
    const propertyValue = (variant as VariantWithDynamicAttributes)[name]
    return {
      ...acc,
      ...(propertyValue && typeof propertyValue === 'string'
        ? { [name]: propertyValue }
        : {})
    }
  }, {} as VariantValues)

const calcAttrData = (
  propName: string,
  accData: AttrDataAux,
  data: {
    variantAttrValues: VariantAttrValues
    attrList: AttributeSelector[]
    attrIndex: number
  }
): AttrDataAux => {
  if (propName) {
    const { values: accValues } = { ...accData }
    const { variantAttrValues, attrList, attrIndex } = { ...data }
    const value = variantAttrValues[propName]
    const nextAttrName = attrList[attrIndex + 1]?.name
    const nextAcc = accValues?.[value] ?? {}
    return {
      ...accData,
      propertyName: propName,
      attrIndex,
      values: {
        ...accValues,
        [value]: calcAttrData(nextAttrName, nextAcc, {
          variantAttrValues,
          attrList,
          attrIndex: attrIndex + 1
        })
      }
    }
  }

  return {}
}

const calcAttrSelectors = (
  attrData: AttrDataAux,
  variantAttrValues: VariantAttrValues
): SelectorValues => {
  const { propertyName, attrIndex, values } = { ...attrData }
  if (propertyName) {
    const nestedObj = values?.[variantAttrValues[propertyName]]
    if (nestedObj) {
      return {
        [propertyName]: {
          attrIndex: Number(attrIndex),
          values: Object.keys(values)
        },
        ...calcAttrSelectors(nestedObj, variantAttrValues)
      }
    }
  }
  return {}
}

const calcAttrCurrPrice = (
  variant: ProductDataVariant,
  locale: string,
  formatPriceOptions?: FormatPriceOptions
) => {
  if (variant?.price?.priceDiscount?.value) {
    return formatPrice({
      value: Number(variant?.price.priceDiscount?.value),
      currency: variant?.price?.priceCurrency,
      locale,
      options: formatPriceOptions
    })
  }
  return formatPrice({
    value: Number(variant?.price?.grossPrice),
    currency: variant?.price?.priceCurrency,
    locale,
    options: formatPriceOptions
  })
}

const calcAttrVariants = ({
  name,
  index,
  attrSelectorValues,
  stocks,
  variantList,
  variantValues,
  locale,
  markers,
  showPrice = false,
  formatPriceOptions
}: {
  name: string
  index: number
  attrSelectorValues: SelectorValues
  stocks: Stocks
  variantList: ProductDataVariant[]
  variantValues: VariantValues
  locale: string
  markers?: AttributeSelectorMarker[]
  showPrice?: boolean
  formatPriceOptions?: FormatPriceOptions
}) =>
  attrSelectorValues[name]?.values?.map((i) => {
    const variantArray =
      variantList.filter(
        (v: ProductDataVariant) =>
          (v as VariantWithDynamicAttributes)[name] === i &&
          Object.entries(attrSelectorValues)
            .filter(([, value]) => index > value?.attrIndex)
            .every(
              ([key]) =>
                (v as VariantWithDynamicAttributes)[key] === variantValues[key]
            )
      ) ?? []

    const currVariant: ProductDataVariant =
      variantArray?.find((v: ProductDataVariant) =>
        Object.entries(attrSelectorValues)
          .filter(([, value]) => index < value?.attrIndex)
          .every(
            ([key]) =>
              (v as VariantWithDynamicAttributes)[key] ===
              attrSelectorValues[key]?.values?.[0]
          )
      ) ?? variantArray[0]
    const value = currVariant
      ? ((currVariant as VariantWithDynamicAttributes)[name] as string)
      : undefined
    const label = value
      ? (markers?.find((m) => m.value === value)?.label ?? value)
      : undefined
    const stockStatus =
      value && currVariant
        ? getStockLevelStatus(currVariant, stocks)
        : undefined

    // Override to outOfStock if minimumOrderQuantity exceeds available stock
    let resolvedStock = stockStatus
    if (resolvedStock && currVariant) {
      const variantStockLevel: number | undefined =
        (currVariant.globalArticleCode
          ? stocks[currVariant.globalArticleCode]?.stockLevel
          : undefined) ??
        currVariant?.stock?.stockLevel ??
        undefined
      if (
        isMinQuantityExceedsStock(
          currVariant.minimumOrderQuantity,
          variantStockLevel
        )
      ) {
        resolvedStock = StockLevelStatus.outOfStock
      }
    }

    return {
      value: value || '',
      attributeLabel: showPrice
        ? `${label || ''}<br>${calcAttrCurrPrice(currVariant, locale, formatPriceOptions)}`
        : `${label || ''}`,
      stock: resolvedStock ? stockStatusMap[resolvedStock] : undefined,
      property: name
    }
  }) ?? []

export const getAttributeSelectors = ({
  attributeSelectorsProp,
  stocks,
  variantList,
  variant,
  attributeSelectorMarkers,
  locale,
  formatPriceOptions
}: {
  attributeSelectorsProp: AttributeSelector[]
  stocks: Stocks
  variantList: ProductDataVariant[]
  variant: ProductDataVariant
  attributeSelectorMarkers: AttributeSelectorMarker[]
  locale: string
  formatPriceOptions?: FormatPriceOptions
}) => {
  const attrs = [
    { name: COLOR_PROPERTY } as AttributeSelector,
    ...attributeSelectorsProp
  ]?.filter((attr) => {
    const value = (variant as VariantWithDynamicAttributes)[attr.name]
    return value && (typeof value === 'string' || typeof value === 'number')
  })

  const attrSchema = variantList?.reduce(
    (acc, curr) => ({
      ...acc,
      ...calcAttrData(attrs[0]?.name, acc, {
        variantAttrValues: getVariantAttrData(attrs, curr),
        attrList: attrs,
        attrIndex: 0
      })
    }),
    {}
  )
  const variantValues = getVariantAttrData(attrs, variant)
  const attrSelectorValues = calcAttrSelectors(attrSchema, variantValues)
  const selectors = attrs
    .map((a, index) => {
      const { name, label, showPrice } = { ...a }
      const currAttributeValue = (variant as VariantWithDynamicAttributes)[
        name
      ] as string
      return {
        currentValue: currAttributeValue,
        property: name,
        name: label,
        label,
        attributeVariants: calcAttrVariants({
          name,
          index,
          attrSelectorValues,
          stocks,
          variantList,
          variantValues,
          markers: attributeSelectorMarkers,
          showPrice,
          locale,
          formatPriceOptions
        })
      }
    })
    ?.filter(
      (attr) =>
        attr.currentValue &&
        attr.property !== COLOR_PROPERTY &&
        attr?.attributeVariants?.length
    )

  const attributeValues =
    selectors
      ?.map((selector) => ({
        property: selector.property,
        value: (variant as VariantWithDynamicAttributes)[
          selector?.property
        ] as string
      }))
      ?.filter((selector) => selector.value) ?? null

  return {
    attributeValues,
    attributeSelectors: selectors
      ?.map((item) => ({
        ...item,
        attributeVariants:
          item.attributeVariants?.length > 1 ? item.attributeVariants : []
      }))
      ?.filter((attr) => attr?.attributeVariants?.length)
  }
}

export const getCurrentAttributeSelectorValues = (
  attributeSelectorsProp: AttributeSelector[],
  variantList: ProductDataVariant[],
  historyFilters: HistoryFilter[]
) => {
  const matchedVariant = getMatchedVariant(variantList, historyFilters)
  const attrs = [...attributeSelectorsProp]?.filter(
    (attr) =>
      matchedVariant &&
      (matchedVariant as VariantWithDynamicAttributes)[attr.name]
  )
  return attrs?.map((attr) => ({
    property: attr.name,
    value: matchedVariant
      ? ((matchedVariant as VariantWithDynamicAttributes)[attr.name] as string)
      : ''
  }))
}
